import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { useNegativeFeedback } from '../../hooks/analytics/useAnalytics'
import { NegativeFeedbackItem } from '../../types/analyticsTypes'
import NegativeFeedbackDrawer from './NegativeFeedbackDrawer'

const PAGE_SIZE = 25

// Timeframe presets. The visibility window is a compliance cap enforced
// server-side (NEGATIVE_FEEDBACK_VISIBILITY_DAYS in backend config); these
// presets must stay within it, and both move together when the policy changes.
const TIMEFRAME_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
]
const DEFAULT_TIMEFRAME_DAYS = 30

const truncate = (value: string | null, maxLength: number): string => {
  if (!value) {
    return '—'
  }
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value
}

const formatTimestamp = (value: string): string => {
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const NegativeFeedbackTable: React.FC = () => {
  const [offset, setOffset] = useState(0)
  const [days, setDays] = useState(DEFAULT_TIMEFRAME_DAYS)
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null)
  const { data, isLoading, isError } = useNegativeFeedback(PAGE_SIZE, offset, days)

  const items: NegativeFeedbackItem[] = data?.items ?? []
  const total = data?.total ?? 0
  const hasPreviousPage = offset > 0
  const hasNextPage = offset + PAGE_SIZE < total

  return (
    <Box bg='white' borderWidth='1px' borderColor='gray.200' borderRadius='lg' p={5}>
      <Flex justify='space-between' align='flex-start' mb={1}>
        <Heading size='sm'>Negative feedback</Heading>
        <Select
          size='sm'
          width='auto'
          value={days}
          onChange={event => {
            setDays(Number(event.target.value))
            setOffset(0)
          }}
        >
          {TIMEFRAME_OPTIONS.map(option => (
            <option key={option.days} value={option.days}>
              {option.label}
            </option>
          ))}
        </Select>
      </Flex>
      <Text fontSize='sm' color='gray.500' mb={4}>
        {total} thumbs-down answer{total === 1 ? '' : 's'} in this period. Click a row for the conversation.
      </Text>

      {isLoading && (
        <Flex justify='center' py={10}>
          <Spinner />
        </Flex>
      )}
      {isError && <Text color='red.500'>Could not load negative feedback.</Text>}

      {!isLoading && !isError && (
        <>
          <Box overflowX='auto'>
            <Table size='sm'>
              <Thead>
                <Tr>
                  <Th>User</Th>
                  <Th>When</Th>
                  <Th>Question</Th>
                  <Th>Answer</Th>
                  <Th>Reason</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.length === 0 && (
                  <Tr>
                    <Td colSpan={5}>
                      <Text color='gray.500' py={4}>
                        No negative feedback yet.
                      </Text>
                    </Td>
                  </Tr>
                )}
                {items.map(item => (
                  <Tr
                    key={item.message_id}
                    cursor='pointer'
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => setSelectedMessageId(item.message_id)}
                  >
                    <Td>{item.user_id ?? '—'}</Td>
                    <Td whiteSpace='nowrap'>{formatTimestamp(item.created_at)}</Td>
                    <Td>{truncate(item.question, 60)}</Td>
                    <Td>{truncate(item.answer, 60)}</Td>
                    <Td>{truncate(item.reason_bad_response, 30)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <HStack justify='flex-end' mt={4} spacing={3}>
            <Text fontSize='sm' color='gray.500'>
              {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </Text>
            <Button
              size='sm'
              variant='outline'
              isDisabled={!hasPreviousPage}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              size='sm'
              variant='outline'
              isDisabled={!hasNextPage}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </HStack>
        </>
      )}

      <NegativeFeedbackDrawer
        messageId={selectedMessageId}
        isOpen={selectedMessageId !== null}
        onClose={() => setSelectedMessageId(null)}
      />
    </Box>
  )
}

export default NegativeFeedbackTable
